import React, { useMemo, useState } from "react";

/**
 * RAT-BAT Demo Page
 * Risk And Task Based Aptitude Test
 *
 * Demo modes:
 * 1. Short candidate demo - Category 1 only
 * 2. Full TOC sample report - all 7 categories / 26 NTS populated
 *
 * Bar colour logic:
 * - Green = good / low concern
 * - Amber = mixed / needs probing
 * - Red = poor / high concern
 * - Risk bars are inverted so low-risk results sit in the green area.
 */

const NTS_FRAMEWORK = [
  {
    id: "cat1",
    category: "1. Situational awareness",
    summary: "Aware of what is going on and able to anticipate what could happen.",
    skills: [
      { id: "1.1", name: "Attention to detail", descriptor: "Can focus on details and identify unusual or unexpected things." },
      { id: "1.2", name: "Overall awareness", descriptor: "Can step back from the detail to monitor and understand the overall situation." },
      { id: "1.3", name: "Maintain concentration", descriptor: "Uses techniques to stay alert and manage distraction." },
      { id: "1.4", name: "Retain information during shift", descriptor: "Uses techniques to remember and recall information." },
      { id: "1.5", name: "Anticipation of risk", descriptor: "Is alert to hazards, risks and error traps, and responds appropriately." },
    ],
  },
  {
    id: "cat2",
    category: "2. Diligence",
    summary: "Careful, thorough, consistent and logical when completing a task.",
    skills: [
      { id: "2.1", name: "Systematic and thorough approach", descriptor: "Completes tasks using a logical and thorough approach." },
      { id: "2.2", name: "Checking", descriptor: "Checks actions and information rather than making assumptions." },
      { id: "2.3", name: "Follows rules and procedures", descriptor: "Complies with rules, procedures and authorised ways of working." },
    ],
  },
  {
    id: "cat3",
    category: "3. Communication",
    summary: "Communicating effectively through spoken, written and non-verbal means.",
    skills: [
      { id: "3.1", name: "Listening", descriptor: "Listens, understands and responds appropriately." },
      { id: "3.2", name: "Clarity", descriptor: "Communicates clearly, concisely and accurately." },
      { id: "3.3", name: "Assertiveness", descriptor: "Speaks up and challenges when required." },
      { id: "3.4", name: "Sharing information", descriptor: "Shares the right information with the right person at the right time." },
    ],
  },
  {
    id: "cat4",
    category: "4. Decision making and action",
    summary: "Making decisions and taking action within the area of authority.",
    skills: [
      { id: "4.1", name: "Effective decisions", descriptor: "Weighs up information and options to make a safe decision." },
      { id: "4.2", name: "Timely decisions", descriptor: "Makes decisions and takes action at the right time." },
      { id: "4.3", name: "Diagnosing problems", descriptor: "Establishes the cause of problems before acting." },
    ],
  },
  {
    id: "cat5",
    category: "5. Cooperation and working with others",
    summary: "Working with others in a positive, respectful and supportive manner.",
    skills: [
      { id: "5.1", name: "Considering others’ needs", descriptor: "Considers the views and needs of others." },
      { id: "5.2", name: "Supporting others", descriptor: "Cooperates, supports others and takes the lead when required." },
      { id: "5.3", name: "Treating others with respect", descriptor: "Is respectful, polite and professional." },
      { id: "5.4", name: "Dealing with conflict/aggressive behaviour", descriptor: "Recognises and helps address challenging behaviour and conflict." },
    ],
  },
  {
    id: "cat6",
    category: "6. Workload management",
    summary: "Managing and prioritising workload under normal and pressured conditions.",
    skills: [
      { id: "6.1", name: "Selective attention", descriptor: "Can switch between different information and tasks when needed." },
      { id: "6.2", name: "Prioritising", descriptor: "Can prioritise information and tasks appropriately." },
      { id: "6.3", name: "Calm under pressure", descriptor: "Uses techniques to remain calm under pressure." },
    ],
  },
  {
    id: "cat7",
    category: "7. Self-management",
    summary: "Motivated, confident and prepared to do the job as well as possible.",
    skills: [
      { id: "7.1", name: "Motivation", descriptor: "Performs beyond the minimum requirements of the role." },
      { id: "7.2", name: "Confidence and initiative", descriptor: "Can work independently but asks for help when required." },
      { id: "7.3", name: "Maintain and develop skills knowledge", descriptor: "Keeps knowledge and skills up to date." },
      { id: "7.4", name: "Prepared and organised", descriptor: "Is prepared and organised for work." },
    ],
  },
];

const SCORING_DIMENSIONS = {
  ruleAdherence: { label: "Rule adherence", weight: 1.35 },
  riskAwareness: { label: "Risk awareness", weight: 1.45 },
  attentionToDetail: { label: "Attention to detail", weight: 1.25 },
  checkingBehaviour: { label: "Checking behaviour", weight: 1.35 },
  escalation: { label: "Escalation", weight: 1.2 },
  communication: { label: "Communication", weight: 1.0 },
  delayPressure: { label: "Resistance to delay pressure", weight: 1.45 },
  ownership: { label: "Operational ownership", weight: 1.25 },
  workloadManagement: { label: "Workload management", weight: 1.15 },
  learningCulture: { label: "Reporting / learning culture", weight: 1.1 },
};

const RISK_TRAITS = {
  servicePressureSusceptibility: { label: "Service pressure susceptibility", weight: 1.4 },
  ruleBypassTolerance: { label: "Rule-bypass tolerance", weight: 1.45 },
  assumptionRisk: { label: "Assumption risk", weight: 1.35 },
  escalationAvoidance: { label: "Escalation avoidance", weight: 1.25 },
  overconfidence: { label: "Overconfidence", weight: 1.2 },
  socialPressureCompliance: { label: "Social pressure compliance", weight: 1.15 },
  reportingReluctance: { label: "Reporting reluctance", weight: 1.3 },
};

const MATURITY_BANDS = [
  {
    min: 4.5,
    label: "Very strong operational risk maturity",
    decision: "Progress",
    colour: "emerald",
    summary: "Strong safety judgement, low risk-trait exposure and strong NTS consistency.",
  },
  {
    min: 3.8,
    label: "Good operational risk maturity",
    decision: "Progress with probing",
    colour: "blue",
    summary: "Generally positive profile with one or more areas suitable for targeted interview probing.",
  },
  {
    min: 3.0,
    label: "Developing / mixed operational risk maturity",
    decision: "Hold for review",
    colour: "amber",
    summary: "Mixed profile. Some safe judgement shown, but enough variation to require structured review.",
  },
  {
    min: 2.2,
    label: "Elevated operational risk profile",
    decision: "Caution",
    colour: "orange",
    summary: "Possible susceptibility to pressure, assumptions, incomplete checking or informal workarounds.",
  },
  {
    min: 1,
    label: "High operational risk profile",
    decision: "Do not progress",
    colour: "red",
    summary: "High-risk decision pattern for safety-critical work.",
  },
];

const SHORT_DEMO_QUESTIONS = [
  {
    id: "q1",
    ntsId: "1.1",
    title: "Train reporting number mismatch",
    scenario:
      "You are preparing to work train 2F25. The service is due to depart in six minutes. During preparation you check the departure board, diagram information, route details and GSM-R setup. Your schedule shows 2F25, but the GSM-R registration screen now displays 2F52. The platform is busy and the dispatcher is clearly trying to avoid a delay.",
    question: "What is the most appropriate response?",
    answers: [
      {
        id: "q1a",
        text: "Pause the departure process, verify the reporting number and route information, and resolve the mismatch before departing.",
        score: 5,
        profile: "excellent",
        flags: ["hidden mismatch detected", "safety over service", "checks before movement"],
        assessorNote: "Strong response. The candidate identifies the hidden mismatch and prevents movement until the discrepancy is resolved.",
        probe: "Ask how they would manage dispatcher and passenger pressure while resolving the mismatch.",
      },
      {
        id: "q1b",
        text: "Ask the dispatcher to confirm whether the service is definitely 2F25, then depart if they say it is correct.",
        score: 3,
        profile: "borderline",
        flags: ["partial check", "delegates ownership", "informal confirmation"],
        assessorNote: "Borderline response. The candidate notices the issue but relies on informal confirmation rather than resolving the operational discrepancy.",
        probe: "Ask why dispatcher confirmation may not be sufficient for a cab-system or train-identity discrepancy.",
      },
      {
        id: "q1c",
        text: "Depart on time because the diagram and platform information show 2F25, then correct the GSM-R details when workload allows.",
        score: 2,
        profile: "elevated",
        flags: ["continues with known discrepancy", "service pressure", "late correction"],
        assessorNote: "Elevated risk. The candidate allows punctuality pressure to override safety assurance.",
        probe: "Ask what could happen if the reporting number or route information is wrong and the train departs.",
      },
      {
        id: "q1d",
        text: "Assume it is only a minor input error because the route is familiar and the train is ready to depart.",
        score: 1,
        profile: "critical",
        flags: ["unsafe assumption", "normalisation of deviation", "no escalation"],
        assessorNote: "High risk. The candidate normalises an unresolved safety-critical discrepancy.",
        probe: "Ask the candidate to explain the difference between experience-based confidence and unsafe assumption.",
      },
      {
        id: "q1e",
        text: "Depart but make a mental note to mention it later if anyone questions the GSM-R registration.",
        score: 1,
        profile: "critical",
        flags: ["known issue not reported", "reactive reporting", "safety overridden"],
        assessorNote: "High risk. The candidate recognises a discrepancy but chooses not to act until challenged.",
        probe: "Ask why making a mental note is not a safe substitute for immediate verification.",
      },
    ],
  },
  {
    id: "q2",
    ntsId: "1.2",
    title: "Conflicting information before departure",
    scenario:
      "You are booked to depart from platform 3. The signal is showing a proceed aspect and the dispatcher is preparing to give the tip. You notice that the customer information screen is showing a different destination from your diagram. The platform announcement also refers to a stopping pattern that does not match your schedule.",
    question: "What would you do?",
    answers: [
      {
        id: "q2a",
        text: "Hold the train, check the working with the correct railway contact, and only depart once the train, route and stopping pattern are confirmed.",
        score: 5,
        profile: "excellent",
        flags: ["conflicting cues resolved", "pre-departure verification", "broad situational awareness"],
        assessorNote: "Strong response. The candidate treats conflicting information as a cue to verify before movement.",
        probe: "Ask how they would explain the short delay to platform staff and control.",
      },
      {
        id: "q2b",
        text: "Ask the dispatcher whether the passengers are on the correct train and depart if they appear confident.",
        score: 3,
        profile: "borderline",
        flags: ["informal confirmation", "partial verification"],
        assessorNote: "Mixed response. The candidate notices the cue but checks informally rather than resolving the operational conflict.",
        probe: "Ask what source of information should be treated as authoritative.",
      },
      {
        id: "q2c",
        text: "Depart because the signal is clear and the dispatcher is responsible for platform information.",
        score: 2,
        profile: "elevated",
        flags: ["narrow focus", "delegates ownership", "unresolved conflict"],
        assessorNote: "Elevated risk. The candidate focuses on one cue and ignores broader conflicting information.",
        probe: "Ask how customer information can indicate a wider planning or route issue.",
      },
      {
        id: "q2d",
        text: "Ignore the customer information because passenger systems are often wrong and operational systems matter more.",
        score: 2,
        profile: "elevated",
        flags: ["dismissed warning cue", "assumption-led", "no checking"],
        assessorNote: "Elevated risk. The candidate dismisses a possible warning cue because it conflicts with expectation.",
        probe: "Ask about previous situations where apparently minor conflicting information revealed a real operational issue.",
      },
      {
        id: "q2e",
        text: "Depart and listen for any passenger complaints after leaving the station.",
        score: 1,
        profile: "critical",
        flags: ["reactive only", "known uncertainty ignored", "safety risk deferred"],
        assessorNote: "High risk. The candidate waits for consequences rather than controlling risk before movement.",
        probe: "Ask why post-departure correction is weaker than pre-departure prevention.",
      },
    ],
  },
  {
    id: "q3",
    ntsId: "1.3",
    title: "Non-urgent call during degraded working",
    scenario:
      "You are driving under a degraded method of working after a signalling irregularity. The signaller has given you specific instructions, including a speed restriction and a defined limit of authority. Shortly afterwards, the cab radio receives a non-urgent call from control asking for an update on passenger numbers and expected delay. You are approaching the location where the degraded instructions become critical.",
    question: "What is the best response?",
    answers: [
      {
        id: "q3a",
        text: "Prioritise the degraded working instructions, maintain concentration, and respond to the non-urgent call only when it is safe to do so.",
        score: 5,
        profile: "excellent",
        flags: ["protects attention", "task prioritisation", "safe communication timing"],
        assessorNote: "Strong response. The candidate protects concentration during a safety-critical phase.",
        probe: "Ask how they decide whether a communication is urgent enough to answer immediately.",
      },
      {
        id: "q3b",
        text: "Answer briefly while continuing, keeping the response short so concentration is not affected for long.",
        score: 3,
        profile: "borderline",
        flags: ["divided attention", "underestimated workload"],
        assessorNote: "Borderline response. The candidate attempts to manage the distraction but still accepts avoidable divided attention.",
        probe: "Ask what can happen to instruction retention during a short but badly timed call.",
      },
      {
        id: "q3c",
        text: "Ask control to repeat the question because you are also trying to remember the signaller’s instructions.",
        score: 2,
        profile: "elevated",
        flags: ["cognitive overload", "critical task interruption"],
        assessorNote: "Elevated risk. The candidate allows a non-urgent interaction to compete with safety-critical instruction retention.",
        probe: "Ask how they would reduce workload before reaching the critical location.",
      },
      {
        id: "q3d",
        text: "Continue the call because control needs accurate delay information and passengers will expect updates.",
        score: 2,
        profile: "elevated",
        flags: ["service pressure", "loss of task priority", "customer focus over safety-critical task"],
        assessorNote: "Elevated risk. The candidate prioritises service information over immediate safety-critical driving demands.",
        probe: "Ask how they distinguish customer-service pressure from safety-critical priority.",
      },
      {
        id: "q3e",
        text: "Try to deal with the call, the restriction and the limit of authority at the same time because experienced drivers should be able to multitask.",
        score: 1,
        profile: "critical",
        flags: ["unsafe multitasking", "overconfidence", "critical attention failure"],
        assessorNote: "High risk. The candidate shows overconfidence and poor appreciation of workload limits.",
        probe: "Ask the candidate to explain the difference between confidence and safe workload management.",
      },
    ],
  },
  {
    id: "q4",
    ntsId: "1.4",
    title: "Remembering an amended instruction",
    scenario:
      "At the start of your shift you read a safety notice about a temporary change at a location on your route. Later, during the journey, you approach that area. You remember that there was an amendment, but you cannot confidently recall the full detail. The service is running late and you believe the change probably relates to another platform or line.",
    question: "What is the most appropriate response?",
    answers: [
      {
        id: "q4a",
        text: "Treat the uncertainty as operationally relevant, use the proper source or contact the appropriate railway contact to confirm the instruction before relying on memory.",
        score: 5,
        profile: "excellent",
        flags: ["memory uncertainty recognised", "checks source information", "does not rely on assumption"],
        assessorNote: "Strong response. The candidate recognises memory uncertainty and verifies before acting.",
        probe: "Ask what techniques they use to retain and recall safety information during a shift.",
      },
      {
        id: "q4b",
        text: "Reduce workload, proceed cautiously within authority, and confirm the instruction at the first safe opportunity before reaching the affected point.",
        score: 4,
        profile: "good",
        flags: ["safe opportunity", "manages workload", "checks before affected point"],
        assessorNote: "Good response. The candidate plans to verify before reaching the affected location, although timing must be managed carefully.",
        probe: "Ask what would change if the affected location was closer than expected.",
      },
      {
        id: "q4c",
        text: "Carry on but stay extra alert because you remember enough to know there was only a minor change.",
        score: 3,
        profile: "borderline",
        flags: ["memory reliance", "minor-change assumption", "no confirmation"],
        assessorNote: "Borderline to elevated risk. The candidate recognises there was an amendment but relies on incomplete recall.",
        probe: "Ask why partial memory can be more dangerous than no memory of the instruction at all.",
      },
      {
        id: "q4d",
        text: "Assume the change does not apply because you would have remembered it clearly if it affected your train.",
        score: 2,
        profile: "elevated",
        flags: ["unsafe memory assumption", "no checking", "overconfidence"],
        assessorNote: "Elevated risk. The candidate assumes non-applicability because they cannot recall the detail.",
        probe: "Ask how expectation and familiarity can distort memory during routine work.",
      },
      {
        id: "q4e",
        text: "Ignore it for now because checking would cause delay and the instruction can be reviewed properly after the trip.",
        score: 1,
        profile: "critical",
        flags: ["delay pressure", "known uncertainty ignored", "deferred safety check"],
        assessorNote: "High risk. The candidate knowingly defers a potentially safety-critical check until after the risk has passed.",
        probe: "Ask why checking after the trip does not control the immediate operational risk.",
      },
    ],
  },
  {
    id: "q5",
    ntsId: "1.5",
    title: "Low adhesion and time pressure",
    scenario:
      "You are approaching a station on a falling gradient. Railhead conditions are poor and there have been recent reports of low adhesion in the area. You are running three minutes late. The defensive driving policy expects earlier braking in these conditions. You believe you can still make the booked stopping point if braking is left slightly later than usual.",
    question: "Which response best reflects mature operational risk judgement?",
    answers: [
      {
        id: "q5a",
        text: "Brake earlier, manage the approach defensively, and accept any minor delay rather than reducing the safety margin.",
        score: 5,
        profile: "excellent",
        flags: ["anticipates risk", "defensive driving", "safety margin preserved"],
        assessorNote: "Strong response. The candidate anticipates foreseeable risk and preserves braking margin.",
        probe: "Ask how they would explain the delay if challenged by control.",
      },
      {
        id: "q5b",
        text: "Use normal braking but remain ready to apply a stronger brake if the train does not respond as expected.",
        score: 3,
        profile: "borderline",
        flags: ["reactive control", "reduced margin"],
        assessorNote: "Mixed response. The candidate recognises the risk but relies on later correction rather than early margin.",
        probe: "Ask why early defensive control is stronger than waiting to see whether braking is effective.",
      },
      {
        id: "q5c",
        text: "Brake slightly later to recover time because previous low-adhesion reports do not always mean the railhead is poor now.",
        score: 2,
        profile: "elevated",
        flags: ["delay recovery bias", "risk discounting", "reduced safety margin"],
        assessorNote: "Elevated risk. The candidate discounts known risk information to recover time.",
        probe: "Ask how they would assess low-frequency but high-consequence risk.",
      },
      {
        id: "q5d",
        text: "Approach as normal because the train has modern braking systems and will intervene if needed.",
        score: 1,
        profile: "critical",
        flags: ["automation bias", "unsafe assumption", "reduced ownership"],
        assessorNote: "High risk. The candidate relies on systems rather than applying defensive driving responsibility.",
        probe: "Ask what engineered systems do not protect against in low-adhesion scenarios.",
      },
      {
        id: "q5e",
        text: "Keep the booked braking point because changing driving style too much may create unnecessary delay.",
        score: 2,
        profile: "elevated",
        flags: ["inflexible risk response", "service pressure", "does not adapt to conditions"],
        assessorNote: "Elevated risk. The candidate does not sufficiently adapt to degraded environmental risk.",
        probe: "Ask how driving style should change when conditions change.",
      },
    ],
  },
];

const FULL_DEMO_SCORE_PATTERNS = {
  "Current candidate": {
    "1.1": 4.4,
    "1.2": 4.2,
    "1.3": 3.8,
    "1.4": 4.0,
    "1.5": 3.4,
    "2.1": 4.1,
    "2.2": 3.5,
    "2.3": 3.6,
    "3.1": 4.0,
    "3.2": 3.9,
    "3.3": 3.4,
    "3.4": 3.7,
    "4.1": 3.8,
    "4.2": 3.6,
    "4.3": 3.5,
    "5.1": 4.3,
    "5.2": 4.0,
    "5.3": 4.5,
    "5.4": 3.5,
    "6.1": 3.6,
    "6.2": 3.8,
    "6.3": 3.4,
    "7.1": 4.2,
    "7.2": 3.7,
    "7.3": 4.1,
    "7.4": 4.0,
  },
  "A. Khan": {
    "1.1": 4.8,
    "1.2": 4.7,
    "1.3": 4.5,
    "1.4": 4.6,
    "1.5": 4.7,
    "2.1": 4.8,
    "2.2": 4.6,
    "2.3": 4.9,
    "3.1": 4.5,
    "3.2": 4.4,
    "3.3": 4.3,
    "3.4": 4.6,
    "4.1": 4.7,
    "4.2": 4.5,
    "4.3": 4.4,
    "5.1": 4.6,
    "5.2": 4.5,
    "5.3": 4.8,
    "5.4": 4.3,
    "6.1": 4.5,
    "6.2": 4.6,
    "6.3": 4.4,
    "7.1": 4.7,
    "7.2": 4.5,
    "7.3": 4.6,
    "7.4": 4.8,
  },
  "J. Miller": {
    "1.1": 4.1,
    "1.2": 4.2,
    "1.3": 4.0,
    "1.4": 4.1,
    "1.5": 3.3,
    "2.1": 4.0,
    "2.2": 3.7,
    "2.3": 3.8,
    "3.1": 4.1,
    "3.2": 4.0,
    "3.3": 3.7,
    "3.4": 3.8,
    "4.1": 3.9,
    "4.2": 3.7,
    "4.3": 3.6,
    "5.1": 4.2,
    "5.2": 4.1,
    "5.3": 4.3,
    "5.4": 3.7,
    "6.1": 3.7,
    "6.2": 3.6,
    "6.3": 3.3,
    "7.1": 4.1,
    "7.2": 3.9,
    "7.3": 4.0,
    "7.4": 4.1,
  },
  "B. Smith": {
    "1.1": 3.2,
    "1.2": 3.5,
    "1.3": 3.3,
    "1.4": 3.6,
    "1.5": 2.9,
    "2.1": 3.2,
    "2.2": 2.7,
    "2.3": 2.8,
    "3.1": 3.5,
    "3.2": 3.4,
    "3.3": 3.0,
    "3.4": 3.1,
    "4.1": 3.0,
    "4.2": 3.1,
    "4.3": 3.2,
    "5.1": 3.8,
    "5.2": 3.6,
    "5.3": 3.9,
    "5.4": 3.2,
    "6.1": 3.0,
    "6.2": 2.9,
    "6.3": 2.8,
    "7.1": 3.6,
    "7.2": 3.1,
    "7.3": 3.5,
    "7.4": 3.4,
  },
  "C. Jones": {
    "1.1": 2.1,
    "1.2": 2.4,
    "1.3": 2.2,
    "1.4": 2.7,
    "1.5": 2.0,
    "2.1": 2.3,
    "2.2": 2.1,
    "2.3": 1.9,
    "3.1": 2.8,
    "3.2": 2.7,
    "3.3": 2.2,
    "3.4": 2.3,
    "4.1": 2.3,
    "4.2": 2.4,
    "4.3": 2.5,
    "5.1": 3.0,
    "5.2": 2.8,
    "5.3": 3.1,
    "5.4": 2.4,
    "6.1": 2.2,
    "6.2": 2.0,
    "6.3": 2.1,
    "7.1": 2.9,
    "7.2": 2.3,
    "7.3": 2.8,
    "7.4": 2.6,
  },
  "D. Evans": {
    "1.1": 3.7,
    "1.2": 3.8,
    "1.3": 4.0,
    "1.4": 3.9,
    "1.5": 3.7,
    "2.1": 3.9,
    "2.2": 3.8,
    "2.3": 4.0,
    "3.1": 3.2,
    "3.2": 3.1,
    "3.3": 2.8,
    "3.4": 3.0,
    "4.1": 3.7,
    "4.2": 3.5,
    "4.3": 3.6,
    "5.1": 3.3,
    "5.2": 3.2,
    "5.3": 3.5,
    "5.4": 2.7,
    "6.1": 3.9,
    "6.2": 3.8,
    "6.3": 3.6,
    "7.1": 3.9,
    "7.2": 3.8,
    "7.3": 3.7,
    "7.4": 4.0,
  },
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function round(value, places = 2) {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(places));
}

function average(values) {
  const valid = values.filter((value) => Number.isFinite(value));
  if (!valid.length) return 0;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function weightedAverage(entries) {
  const valid = entries.filter(
    (entry) => Number.isFinite(entry.value) && Number.isFinite(entry.weight) && entry.weight > 0
  );

  if (!valid.length) return 0;

  const totalWeight = valid.reduce((sum, entry) => sum + entry.weight, 0);
  const total = valid.reduce((sum, entry) => sum + entry.value * entry.weight, 0);

  return total / totalWeight;
}

function flattenNts() {
  return NTS_FRAMEWORK.flatMap((category) =>
    category.skills.map((skill) => ({
      ...skill,
      categoryId: category.id,
      category: category.category,
      categorySummary: category.summary,
    }))
  );
}

function getMaturityBand(score) {
  return MATURITY_BANDS.find((band) => score >= band.min) || MATURITY_BANDS[MATURITY_BANDS.length - 1];
}

function getBandClasses(colour) {
  const classes = {
    emerald: "border-emerald-300/30 bg-emerald-400/10 text-emerald-100",
    blue: "border-blue-300/30 bg-blue-400/10 text-blue-100",
    amber: "border-amber-300/30 bg-amber-400/10 text-amber-100",
    orange: "border-orange-300/30 bg-orange-400/10 text-orange-100",
    red: "border-red-300/30 bg-red-400/10 text-red-100",
    slate: "border-slate-300/20 bg-slate-400/10 text-slate-200",
  };

  return classes[colour] || classes.blue;
}

function percentageFromFive(score) {
  return clamp((score / 5) * 100, 0, 100);
}

function buildFullDemoReport(name, scorePattern, options = {}) {
  const allSkills = flattenNts();

  const ntsScores = allSkills.map((skill) => ({
    ntsId: skill.id,
    nts: `${skill.id} ${skill.name}`,
    categoryId: skill.categoryId,
    category: skill.category,
    descriptor: skill.descriptor,
    assessed: true,
    score: round(scorePattern[skill.id] ?? 3.5, 2),
  }));

  const categoryScores = NTS_FRAMEWORK.map((category) => {
    const scores = ntsScores
      .filter((skill) => skill.categoryId === category.id)
      .map((skill) => skill.score);

    return {
      categoryId: category.id,
      category: category.category,
      assessedCount: category.skills.length,
      totalCount: category.skills.length,
      score: round(average(scores), 2),
    };
  });

  const allNtsAverage = average(ntsScores.map((item) => item.score));
  const lowestNts = [...ntsScores].sort((a, b) => a.score - b.score)[0];
  const strongestNts = [...ntsScores].sort((a, b) => b.score - a.score).slice(0, 5);
  const weakestNts = [...ntsScores].sort((a, b) => a.score - b.score).slice(0, 5);

  const dimensionScores = Object.entries(SCORING_DIMENSIONS).map(([key, config]) => {
    let modifier = 0;

    if (key === "checkingBehaviour") modifier = (scorePattern["2.2"] || allNtsAverage) - allNtsAverage;
    if (key === "ruleAdherence") modifier = (scorePattern["2.3"] || allNtsAverage) - allNtsAverage;
    if (key === "communication") modifier = (scorePattern["3.2"] || allNtsAverage) - allNtsAverage;
    if (key === "delayPressure") modifier = (scorePattern["6.3"] || allNtsAverage) - allNtsAverage;
    if (key === "workloadManagement") modifier = (scorePattern["6.2"] || allNtsAverage) - allNtsAverage;
    if (key === "attentionToDetail") modifier = (scorePattern["1.1"] || allNtsAverage) - allNtsAverage;
    if (key === "riskAwareness") modifier = (scorePattern["1.5"] || allNtsAverage) - allNtsAverage;
    if (key === "ownership") modifier = (scorePattern["7.2"] || allNtsAverage) - allNtsAverage;
    if (key === "learningCulture") modifier = (scorePattern["7.3"] || allNtsAverage) - allNtsAverage;
    if (key === "escalation") modifier = (scorePattern["3.3"] || allNtsAverage) - allNtsAverage;

    return {
      key,
      label: config.label,
      weight: config.weight,
      score: round(clamp(allNtsAverage + modifier * 0.75, 1, 5), 2),
    };
  });

  const weightedDimensionScore = weightedAverage(
    dimensionScores.map((item) => ({
      value: item.score,
      weight: item.weight,
    }))
  );

  const riskTraitScores = Object.entries(RISK_TRAITS).map(([key, config]) => {
    let linkedScore = allNtsAverage;

    if (key === "servicePressureSusceptibility") linkedScore = average([scorePattern["1.5"], scorePattern["6.3"], scorePattern["4.2"]]);
    if (key === "ruleBypassTolerance") linkedScore = average([scorePattern["2.3"], scorePattern["2.1"], scorePattern["7.4"]]);
    if (key === "assumptionRisk") linkedScore = average([scorePattern["1.1"], scorePattern["2.2"], scorePattern["4.3"]]);
    if (key === "escalationAvoidance") linkedScore = average([scorePattern["3.3"], scorePattern["3.4"], scorePattern["4.2"]]);
    if (key === "overconfidence") linkedScore = average([scorePattern["7.2"], scorePattern["4.3"], scorePattern["2.2"]]);
    if (key === "socialPressureCompliance") linkedScore = average([scorePattern["5.4"], scorePattern["3.3"], scorePattern["7.2"]]);
    if (key === "reportingReluctance") linkedScore = average([scorePattern["2.3"], scorePattern["3.4"], scorePattern["7.3"]]);

    return {
      key,
      label: config.label,
      weight: config.weight,
      score: round(clamp(6 - linkedScore, 1, 5), 2),
    };
  });

  const weightedRiskTraitScore = weightedAverage(
    riskTraitScores.map((item) => ({
      value: item.score,
      weight: item.weight,
    }))
  );

  const riskAdjustedMaturity = 6 - weightedRiskTraitScore;

  const overallWeightedScore = weightedAverage([
    { value: allNtsAverage, weight: 1.3 },
    { value: weightedDimensionScore, weight: 1.4 },
    { value: riskAdjustedMaturity, weight: 1.3 },
  ]);

  const criticalAnswers = ntsScores
    .filter((item) => item.score < 2.7)
    .map((item) => ({
      title: item.nts,
      nts: item.nts,
      score: item.score,
      flags: ["low NTS score", item.category],
    }));

  const flags = [
    ...weakestNts
      .filter((item) => item.score < 3)
      .map((item) => ({
        flag: item.nts,
        label: `${item.nts} below threshold`,
        count: 1,
      })),
    ...riskTraitScores
      .filter((item) => item.score >= 3.5)
      .map((item) => ({
        flag: item.key,
        label: item.label,
        count: 1,
      })),
  ];

  const overallBand = getMaturityBand(overallWeightedScore);

  const report = {
    name,
    reportMode: options.reportMode || "Full 26-NTS populated demo",
    answeredCount: 26,
    totalQuestions: 26,
    completionPercent: 100,
    rawAnswerScore: round(allNtsAverage, 2),
    weightedDimensionScore: round(weightedDimensionScore, 2),
    weightedRiskTraitScore: round(weightedRiskTraitScore, 2),
    riskAdjustedMaturity: round(riskAdjustedMaturity, 2),
    overallWeightedScore: round(overallWeightedScore, 2),
    overallPercent: Math.round(percentageFromFive(overallWeightedScore)),
    overallBand,
    dimensionScores,
    riskTraitScores,
    categoryScores,
    ntsScores,
    strongestNts,
    weakestNts,
    highestRiskTraits: [...riskTraitScores].sort((a, b) => b.score - a.score).slice(0, 3),
    strongestDimensions: [...dimensionScores].sort((a, b) => b.score - a.score).slice(0, 3),
    weakestDimensions: [...dimensionScores].sort((a, b) => a.score - b.score).slice(0, 3),
    criticalAnswers,
    flags,
    lowestNts,
    interviewProbes: weakestNts.slice(0, 5).map((item) => ({
      nts: item.nts,
      score: item.score,
      question: item.nts,
      probe: `Probe how the candidate would demonstrate ${item.name || item.nts} in a degraded or pressured operational scenario.`,
    })),
  };

  return {
    ...report,
    selectionIndex: calculateSelectionIndex(report),
  };
}

function buildShortDemoReport(selectedAnswers, candidateName) {
  const scorePattern = { ...FULL_DEMO_SCORE_PATTERNS["Current candidate"] };

  SHORT_DEMO_QUESTIONS.forEach((question) => {
    const answerId = selectedAnswers[question.id];
    const answer = question.answers.find((item) => item.id === answerId);
    if (answer) scorePattern[question.ntsId] = answer.score;
  });

  const report = buildFullDemoReport(candidateName || "Current candidate", scorePattern, {
    reportMode: "Short candidate demo extrapolated into full 26-NTS structure",
  });

  const answered = SHORT_DEMO_QUESTIONS.map((question) => {
    const answerId = selectedAnswers[question.id];
    const answer = question.answers.find((item) => item.id === answerId);

    if (!answer) return null;

    return {
      question: {
        ...question,
        nts: `${question.ntsId} ${flattenNts().find((skill) => skill.id === question.ntsId)?.name || ""}`,
      },
      answer,
    };
  }).filter(Boolean);

  const answerFlags = {};
  answered.forEach(({ answer }) => {
    answer.flags.forEach((flag) => {
      answerFlags[flag] = (answerFlags[flag] || 0) + 1;
    });
  });

  return {
    ...report,
    answered,
    answeredCount: answered.length,
    totalQuestions: SHORT_DEMO_QUESTIONS.length,
    completionPercent: Math.round((answered.length / SHORT_DEMO_QUESTIONS.length) * 100),
    flags: [
      ...report.flags,
      ...Object.entries(answerFlags).map(([flag, count]) => ({
        flag,
        label: flag,
        count,
      })),
    ],
    interviewProbes: answered
      .filter(({ answer }) => answer.score <= 3)
      .map(({ question, answer }) => ({
        nts: question.nts,
        score: answer.score,
        question: question.title,
        probe: answer.probe,
      }))
      .concat(report.interviewProbes)
      .slice(0, 8),
  };
}

function calculateSelectionIndex(report) {
  const criticalPenalty = (report.criticalAnswers?.length || 0) * 0.12;

  const highRiskTraitPenalty =
    (report.highestRiskTraits || []).filter((trait) => trait.score >= 3.5).length * 0.1;

  const lowNtsPenalty =
    (report.ntsScores || []).filter((nts) => nts.assessed !== false && nts.score < 3).length * 0.04;

  const index =
    (report.overallWeightedScore || 1) -
    criticalPenalty -
    highRiskTraitPenalty -
    lowNtsPenalty;

  return round(clamp(index, 1, 5), 2);
}

function getTocDecision(report) {
  const selectionIndex = report.selectionIndex || calculateSelectionIndex(report);
  const criticalCount = report.criticalAnswers?.length || 0;
  const highestRisk = report.highestRiskTraits?.[0];

  if (selectionIndex >= 4.4 && criticalCount === 0) {
    return {
      decision: "Progress",
      tone: "Strong candidate",
      colour: "emerald",
      summary:
        "Strong risk maturity profile across the full NTS framework. No major operational risk concerns detected.",
      action:
        "Progress through the recruitment process. Validate with normal structured interview and wider evidence.",
    };
  }

  if (selectionIndex >= 3.7 && criticalCount <= 2) {
    return {
      decision: "Progress with probing",
      tone: "Suitable with targeted probing",
      colour: "blue",
      summary:
        "Generally positive profile, but one or more areas should be tested at interview.",
      action: `Probe ${highestRisk?.label || "the main risk theme"} before final decision.`,
    };
  }

  if (selectionIndex >= 3 && criticalCount <= 5) {
    return {
      decision: "Hold for review",
      tone: "Mixed risk profile",
      colour: "amber",
      summary:
        "Candidate shows some safe judgement but has enough variation to require structured review.",
      action:
        "Use a Driver Manager or safety specialist review before deciding whether to progress.",
    };
  }

  if (selectionIndex >= 2.2) {
    return {
      decision: "Caution",
      tone: "Elevated operational risk",
      colour: "orange",
      summary:
        "Responses indicate possible susceptibility to unsafe assumptions, pressure or incomplete checking.",
      action:
        "Do not progress without strong counter-evidence from interview, psychometrics or employment history.",
    };
  }

  return {
    decision: "Do not progress",
    tone: "High operational risk",
    colour: "red",
    summary:
      "Assessment indicates a high-risk decision pattern for safety-critical work.",
    action:
      "Do not progress unless there is a clear reason to disregard this result and obtain further evidence.",
  };
}

function buildCohortRows(currentReport, candidateName) {
  const reports = [
    {
      id: "current",
      name: candidateName || "Current candidate",
      report: currentReport,
    },
    ...Object.entries(FULL_DEMO_SCORE_PATTERNS)
      .filter(([name]) => name !== "Current candidate")
      .map(([name, scores]) => ({
        id: name.toLowerCase().replaceAll(" ", "-").replaceAll(".", ""),
        name,
        report: buildFullDemoReport(name, scores),
      })),
  ];

  return reports
    .map((candidate) => {
      const decision = getTocDecision(candidate.report);
      const mainRisk = candidate.report.highestRiskTraits?.[0]?.label || "No dominant risk";
      const weakestNts = candidate.report.weakestNts?.[0] || candidate.report.lowestNts;
      const strongestCategory = [...candidate.report.categoryScores]
        .sort((a, b) => b.score - a.score)[0];
      const weakestCategory = [...candidate.report.categoryScores]
        .sort((a, b) => a.score - b.score)[0];

      return {
        id: candidate.id,
        name: candidate.name,
        report: candidate.report,
        score: candidate.report.overallPercent,
        weightedScore: candidate.report.overallWeightedScore,
        selectionIndex: candidate.report.selectionIndex,
        decision,
        mainRisk,
        weakestNts: weakestNts?.nts || "No assessed NTS",
        weakestNtsScore: weakestNts?.score || null,
        strongestCategory: strongestCategory?.category || "N/A",
        weakestCategory: weakestCategory?.category || "N/A",
        criticalCount: candidate.report.criticalAnswers?.length || 0,
      };
    })
    .sort((a, b) => b.selectionIndex - a.selectionIndex)
    .map((candidate, index) => ({
      ...candidate,
      rank: index + 1,
    }));
}

function Pill({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

/**
 * Gradient score bar.
 *
 * Normal scoring:
 * - 1 = poor/high concern, 5 = strong/low concern
 *
 * Risk scoring:
 * - 1 = low concern, 5 = high concern
 * - so risk scores are inverted for the marker position.
 */
function ScoreBar({ score, risk = false }) {
  const safeScore = Number.isFinite(score) ? score : 0;
  const displayScore = risk ? 6 - safeScore : safeScore;
  const markerPosition = `${percentageFromFive(displayScore)}%`;

  return (
    <div className="relative h-3 rounded-full bg-slate-800">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "linear-gradient(90deg, #ef4444 0%, #f97316 25%, #facc15 50%, #84cc16 72%, #22c55e 100%)",
        }}
      />

      <div
        className="absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border-2 border-white bg-slate-950 shadow-lg shadow-black/40"
        style={{
          left: `calc(${markerPosition} - 10px)`,
        }}
      />
    </div>
  );
}

function MetricCard({ label, value, detail, className = "" }) {
  return (
    <div className={`rounded-3xl border border-white/10 bg-white/[0.04] p-5 ${className}`}>
      <p className="text-sm text-slate-400">{label}</p>
      <p className="mt-2 text-3xl font-bold text-white">{value}</p>
      {detail ? <p className="mt-2 text-sm leading-6 text-slate-300">{detail}</p> : null}
    </div>
  );
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <div className="mb-5">
      {eyebrow ? <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">{eyebrow}</p> : null}
      <h2 className="mt-1 text-2xl font-bold text-white">{title}</h2>
      {description ? <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-400">{description}</p> : null}
    </div>
  );
}

function DecisionBadge({ decision }) {
  return <Pill className={getBandClasses(decision.colour)}>{decision.decision}</Pill>;
}

function CategoryHeatmap({ report }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {report.categoryScores.map((category) => (
        <div key={category.categoryId || category.category} className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-white">{category.category}</p>
              <p className="mt-1 text-xs text-slate-500">
                {category.assessedCount}/{category.totalCount} NTS assessed
              </p>
            </div>
            <Pill className={getBandClasses(getMaturityBand(category.score).colour)}>
              {category.score}/5
            </Pill>
          </div>
          <div className="mt-3">
            <ScoreBar score={category.score} />
          </div>
        </div>
      ))}
    </div>
  );
}

function NtsSkillGrid({ report }) {
  return (
    <div className="space-y-5">
      {NTS_FRAMEWORK.map((category) => {
        const skills = report.ntsScores.filter((skill) => skill.categoryId === category.id);

        return (
          <div key={category.id} className="rounded-3xl border border-white/10 bg-slate-900/80 p-5">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="text-lg font-black text-white">{category.category}</h3>
                <p className="mt-1 text-sm text-slate-400">{category.summary}</p>
              </div>
              <Pill className="border-cyan-300/30 bg-cyan-300/10 text-cyan-100">
                {skills.length}/{category.skills.length} assessed
              </Pill>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {skills.map((skill) => (
                <div key={skill.ntsId} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-white">{skill.nts}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-400">{skill.descriptor}</p>
                    </div>
                    <Pill className={getBandClasses(getMaturityBand(skill.score).colour)}>
                      {skill.score}/5
                    </Pill>
                  </div>
                  <div className="mt-3">
                    <ScoreBar score={skill.score} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IndividualApplicantView({ report, candidateName, organisationName, cohortRows }) {
  const decision = getTocDecision(report);
  const currentRank = cohortRows.find((row) => row.id === "current")?.rank;
  const decisionClasses = getBandClasses(decision.colour);
  const bandClasses = getBandClasses(report.overallBand.colour);

  return (
    <div className="space-y-6">
      <div className={`rounded-3xl border p-6 ${decisionClasses}`}>
        <div className="grid gap-5 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <p className="text-sm font-bold uppercase tracking-[0.2em]">Individual applicant decision summary</p>
            <h2 className="mt-2 text-3xl font-black">{decision.decision}</h2>
            <p className="mt-2 text-sm leading-6 opacity-90">{decision.summary}</p>
            <p className="mt-3 text-sm font-bold">{decision.action}</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="text-sm opacity-80">Selection index</p>
            <p className="mt-1 text-4xl font-black">{report.selectionIndex}/5</p>
            <p className="mt-2 text-xs opacity-75">Adjusted for low NTS scores, critical flags and risk-trait exposure.</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="text-sm opacity-80">Cohort rank</p>
            <p className="mt-1 text-4xl font-black">#{currentRank}</p>
            <p className="mt-2 text-xs opacity-75">Compared with the current application cohort.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <MetricCard label="RAT-BAT score" value={`${report.overallPercent}%`} detail={`${report.overallWeightedScore}/5 weighted score`} />
        <MetricCard label="Selection index" value={`${report.selectionIndex}/5`} detail="Best quick-rank score." />
        <MetricCard label="Critical flags" value={report.criticalAnswers.length} detail="Low NTS or high-risk indicators." />
        <MetricCard label="Main risk theme" value={report.highestRiskTraits?.[0]?.label || "None"} detail="Highest risk-trait exposure." className="lg:col-span-2" />
      </div>

      <div className={`rounded-3xl border p-6 ${bandClasses}`}>
        <p className="text-sm font-bold uppercase tracking-[0.2em]">Risk maturity band</p>
        <h2 className="mt-2 text-2xl font-black">{report.overallBand.label}</h2>
        <p className="mt-2 max-w-4xl text-sm leading-6 opacity-90">{report.overallBand.summary}</p>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <SectionHeading
          eyebrow="Individual profile"
          title={`${candidateName || "Current candidate"} · full NTS profile`}
          description={`Organisation: ${organisationName || "Demo operator"}. This view is intended for interview planning, Driver Manager review and assurance evidence.`}
        />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
            <p className="text-sm font-bold text-white">Strongest NTS</p>
            <div className="mt-3 space-y-2">
              {report.strongestNts.slice(0, 5).map((item) => (
                <div key={item.nts} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-300">{item.nts}</span>
                  <span className="font-bold text-emerald-200">{item.score}/5</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
            <p className="text-sm font-bold text-white">Weakest NTS</p>
            <div className="mt-3 space-y-2">
              {report.weakestNts.slice(0, 5).map((item) => (
                <div key={item.nts} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-300">{item.nts}</span>
                  <span className="font-bold text-amber-200">{item.score}/5</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
            <p className="text-sm font-bold text-white">Highest risk traits</p>
            <div className="mt-3 space-y-3">
              {report.highestRiskTraits.map((item) => (
                <div key={item.key}>
                  <div className="mb-1 flex justify-between gap-3 text-sm">
                    <span className="text-slate-300">{item.label}</span>
                    <span className="font-bold text-white">{item.score}/5</span>
                  </div>
                  <ScoreBar score={item.score} risk />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <SectionHeading
          eyebrow="NTS category profile"
          title="Category heatmap"
          description="The individual applicant view shows the full 7-category spread first, before the detailed 26-skill matrix."
        />
        <CategoryHeatmap report={report} />
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <SectionHeading
          eyebrow="Full NTS detail"
          title="All 26 NTS skill scores"
          description="This is the detailed evidence layer. It should support, not replace, structured interview and wider recruitment evidence."
        />
        <NtsSkillGrid report={report} />
      </div>
    </div>
  );
}

function CohortView({ cohortRows, selectedCandidateId, setSelectedCandidateId }) {
  const selected = cohortRows.find((row) => row.id === selectedCandidateId) || cohortRows[0];

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <SectionHeading
          eyebrow="Application cohort"
          title="Shortlist ranking view"
          description="This is the TOC campaign-level view. It is designed to answer: who should progress, who needs probing, and who presents the greatest operational risk?"
        />

        <div className="grid gap-4">
          {cohortRows.map((candidate) => {
            const isSelected = selected?.id === candidate.id;

            return (
              <button
                key={candidate.id}
                type="button"
                onClick={() => setSelectedCandidateId(candidate.id)}
                className={`w-full rounded-3xl border p-4 text-left transition ${
                  isSelected
                    ? "border-cyan-300/50 bg-cyan-300/10"
                    : "border-white/10 bg-slate-900/80 hover:bg-slate-800"
                }`}
              >
                <div className="grid gap-4 lg:grid-cols-12 lg:items-center">
                  <div className="lg:col-span-1">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Rank</p>
                    <p className="text-2xl font-black text-white">#{candidate.rank}</p>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="text-lg font-black text-white">{candidate.name}</p>
                    <p className="text-xs text-slate-500">
                      {candidate.id === "current" ? "Current assessment" : "Demo applicant"}
                    </p>
                  </div>

                  <div className="lg:col-span-2">
                    <DecisionBadge decision={candidate.decision} />
                    <p className="mt-2 text-xs text-slate-500">{candidate.decision.tone}</p>
                  </div>

                  <div className="lg:col-span-1">
                    <p className="text-xs text-slate-500">Score</p>
                    <p className="text-xl font-black text-white">{candidate.score}%</p>
                  </div>

                  <div className="lg:col-span-1">
                    <p className="text-xs text-slate-500">Index</p>
                    <p className="text-xl font-black text-white">{candidate.selectionIndex}</p>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="text-xs text-slate-500">Main risk</p>
                    <p className="text-sm font-semibold text-slate-200">{candidate.mainRisk}</p>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="text-xs text-slate-500">Weakest NTS</p>
                    <p className="text-sm font-semibold text-slate-200">
                      {candidate.weakestNts}
                      {candidate.weakestNtsScore ? ` (${candidate.weakestNtsScore}/5)` : ""}
                    </p>
                  </div>

                  <div className="lg:col-span-1">
                    <p className="text-xs text-slate-500">Flags</p>
                    <Pill
                      className={
                        candidate.criticalCount >= 6
                          ? "border-red-300/30 bg-red-400/10 text-red-100"
                          : candidate.criticalCount >= 2
                            ? "border-amber-300/30 bg-amber-400/10 text-amber-100"
                            : "border-emerald-300/30 bg-emerald-400/10 text-emerald-100"
                      }
                    >
                      {candidate.criticalCount}
                    </Pill>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selected ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
          <SectionHeading
            eyebrow="Selected applicant"
            title={`${selected.name} · comparison summary`}
            description="This panel lets the TOC quickly inspect the selected applicant without opening the full individual report."
          />

          <div className="grid gap-5 lg:grid-cols-4">
            <MetricCard label="Recommendation" value={selected.decision.decision} detail={selected.decision.action} className="lg:col-span-2" />
            <MetricCard label="Selection index" value={`${selected.selectionIndex}/5`} detail="Penalty-adjusted shortlist score." />
            <MetricCard label="Cohort rank" value={`#${selected.rank}`} detail="Within this application cohort." />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
              <p className="text-sm font-bold text-white">Strongest category</p>
              <p className="mt-2 text-lg font-black text-emerald-200">{selected.strongestCategory}</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
              <p className="text-sm font-bold text-white">Weakest category</p>
              <p className="mt-2 text-lg font-black text-amber-200">{selected.weakestCategory}</p>
            </div>
          </div>

          <div className="mt-6">
            <CategoryHeatmap report={selected.report} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function RatbatTest() {
  const [candidateName, setCandidateName] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [reportMode, setReportMode] = useState("short");
  const [viewMode, setViewMode] = useState("individual");
  const [selectedCandidateId, setSelectedCandidateId] = useState("current");

  const currentQuestion = SHORT_DEMO_QUESTIONS[currentIndex];
  const selectedCurrentAnswer = selectedAnswers[currentQuestion.id];
  const answeredCount = Object.keys(selectedAnswers).length;
  const progressPercent = Math.round((answeredCount / SHORT_DEMO_QUESTIONS.length) * 100);

  const report = useMemo(() => {
    if (reportMode === "full") {
      return buildFullDemoReport(candidateName || "Current candidate", FULL_DEMO_SCORE_PATTERNS["Current candidate"]);
    }

    return buildShortDemoReport(selectedAnswers, candidateName);
  }, [selectedAnswers, candidateName, reportMode]);

  const cohortRows = useMemo(() => {
    return buildCohortRows(report, candidateName);
  }, [report, candidateName]);

  function selectAnswer(answerId) {
    setSelectedAnswers((previous) => ({
      ...previous,
      [currentQuestion.id]: answerId,
    }));
  }

  function goNext() {
    if (!selectedCurrentAnswer) return;

    if (currentIndex < SHORT_DEMO_QUESTIONS.length - 1) {
      setCurrentIndex((previous) => previous + 1);
      return;
    }

    setReportMode("short");
    setShowResults(true);
    setShowIntro(false);
  }

  function goPrevious() {
    if (currentIndex > 0) {
      setCurrentIndex((previous) => previous - 1);
    }
  }

  function resetTest() {
    setSelectedAnswers({});
    setCurrentIndex(0);
    setShowIntro(true);
    setShowResults(false);
    setReportMode("short");
    setViewMode("individual");
    setSelectedCandidateId("current");
  }

  function generateFullTocReport() {
    setReportMode("full");
    setShowIntro(false);
    setShowResults(true);
    setViewMode("individual");
    setSelectedCandidateId("current");
  }

  if (showResults) {
    return (
      <main className="min-h-screen bg-slate-950 text-slate-100">
        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <Pill className="border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
                  RAT-BAT working demo model
                </Pill>

                <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
                  TOC Applicant Assessment
                </h1>

                <p className="mt-3 max-w-4xl text-slate-300">
                  {report.reportMode}. Use the individual view for applicant review and the cohort view for campaign ranking.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Pill className="border-white/10 bg-white/5 text-slate-300">
                    Candidate: {candidateName || "Current candidate"}
                  </Pill>
                  <Pill className="border-white/10 bg-white/5 text-slate-300">
                    Organisation: {organisationName || "Demo operator"}
                  </Pill>
                  <Pill className="border-emerald-300/20 bg-emerald-400/10 text-emerald-100">
                    7 categories / 26 NTS populated
                  </Pill>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <button
                  type="button"
                  onClick={() => setViewMode("individual")}
                  className={`rounded-2xl px-5 py-3 text-sm font-bold transition ${
                    viewMode === "individual"
                      ? "bg-cyan-300 text-slate-950"
                      : "border border-white/10 bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  Individual view
                </button>

                <button
                  type="button"
                  onClick={() => setViewMode("cohort")}
                  className={`rounded-2xl px-5 py-3 text-sm font-bold transition ${
                    viewMode === "cohort"
                      ? "bg-cyan-300 text-slate-950"
                      : "border border-white/10 bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  Application cohort view
                </button>

                <button
                  type="button"
                  onClick={resetTest}
                  className="rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/15"
                >
                  Restart demo
                </button>
              </div>
            </div>
          </div>

          {viewMode === "individual" ? (
            <IndividualApplicantView
              report={report}
              candidateName={candidateName}
              organisationName={organisationName}
              cohortRows={cohortRows}
            />
          ) : (
            <CohortView
              cohortRows={cohortRows}
              selectedCandidateId={selectedCandidateId}
              setSelectedCandidateId={setSelectedCandidateId}
            />
          )}
        </section>
      </main>
    );
  }

  if (showIntro) {
    return (
      <main className="min-h-screen bg-slate-950 text-slate-100">
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl shadow-black/30">
            <Pill className="border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
              RAT-BAT demo
            </Pill>

            <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-6xl">
              Risk And Task Based Aptitude Test
            </h1>

            <p className="mt-5 max-w-4xl text-lg leading-8 text-slate-300">
              This working model demonstrates how RAT-BAT can assess operational risk maturity using realistic railway scenarios, hidden 1–5 scoring, NTS mapping, TOC decision summaries and cohort comparison.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                <p className="text-lg font-bold text-white">Full NTS framework</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  All 7 categories and 26 NTS skills are included in the report model.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                <p className="text-lg font-bold text-white">Individual view</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Summarises one applicant’s decision, score, risk themes and NTS profile.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                <p className="text-lg font-bold text-white">Cohort view</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Ranks applicants by selection index, not raw score alone.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                <p className="text-lg font-bold text-white">TOC decision support</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Designed to support structured recruitment, not replace professional judgement.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold text-slate-300">Candidate name</span>
                <input
                  type="text"
                  value={candidateName}
                  onChange={(event) => setCandidateName(event.target.value)}
                  placeholder="Current candidate"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none ring-0 placeholder:text-slate-600 focus:border-cyan-300"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-300">Organisation</span>
                <input
                  type="text"
                  value={organisationName}
                  onChange={(event) => setOrganisationName(event.target.value)}
                  placeholder="Demo operator"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none ring-0 placeholder:text-slate-600 focus:border-cyan-300"
                />
              </label>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => setShowIntro(false)}
                className="rounded-2xl bg-cyan-300 px-6 py-4 text-sm font-black text-slate-950 transition hover:bg-cyan-200"
              >
                Start short candidate demo
              </button>

              <button
                type="button"
                onClick={generateFullTocReport}
                className="rounded-2xl border border-white/10 bg-white/10 px-6 py-4 text-sm font-bold text-white transition hover:bg-white/15"
              >
                Generate full 26-NTS TOC report
              </button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <Pill className="border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
                RAT-BAT candidate assessment
              </Pill>

              <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
                Category 1 Demo Test
              </h1>

              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
                This short demo assesses the five NTS skills under Category 1: Situational awareness. The sample TOC report then shows how a full 26-NTS assessment would be presented.
              </p>
            </div>

            <button
              type="button"
              onClick={resetTest}
              className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/15"
            >
              Reset
            </button>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between text-sm text-slate-400">
              <span>
                Progress: {answeredCount}/{SHORT_DEMO_QUESTIONS.length}
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">
                Question {currentIndex + 1} of {SHORT_DEMO_QUESTIONS.length}
              </p>

              <h2 className="mt-2 text-2xl font-black text-white">{currentQuestion.title}</h2>

              <div className="mt-3 flex flex-wrap gap-2">
                <Pill className="border-white/10 bg-white/5 text-slate-300">1. Situational awareness</Pill>
                <Pill className="border-purple-300/20 bg-purple-300/10 text-purple-100">
                  {currentQuestion.ntsId} {flattenNts().find((skill) => skill.id === currentQuestion.ntsId)?.name}
                </Pill>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-4 text-sm leading-6 text-slate-400 md:max-w-xs">
              Scoring is hidden from the candidate. The TOC report is generated after completion.
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/80 p-5">
            <p className="text-sm font-bold text-white">Scenario</p>
            <p className="mt-2 leading-7 text-slate-300">{currentQuestion.scenario}</p>
          </div>

          <div className="mt-6">
            <p className="text-lg font-bold text-white">{currentQuestion.question}</p>

            <div className="mt-4 space-y-3">
              {currentQuestion.answers.map((answer) => {
                const isSelected = selectedCurrentAnswer === answer.id;

                return (
                  <button
                    key={answer.id}
                    type="button"
                    onClick={() => selectAnswer(answer.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      isSelected
                        ? "border-cyan-300 bg-cyan-300/10 shadow-lg shadow-cyan-950/30"
                        : "border-white/10 bg-slate-900/70 hover:border-white/20 hover:bg-slate-800"
                    }`}
                  >
                    <div className="flex gap-3">
                      <div
                        className={`mt-1 h-5 w-5 shrink-0 rounded-full border ${
                          isSelected ? "border-cyan-200 bg-cyan-300" : "border-slate-600"
                        }`}
                      />
                      <p className="leading-6 text-slate-200">{answer.text}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={goPrevious}
              disabled={currentIndex === 0}
              className="rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>

            {!selectedCurrentAnswer ? (
              <p className="rounded-2xl border border-amber-300/20 bg-amber-300/10 px-4 py-3 text-sm text-amber-100">
                Select an answer to continue.
              </p>
            ) : null}

            <button
              type="button"
              onClick={goNext}
              disabled={!selectedCurrentAnswer}
              className="rounded-2xl bg-cyan-300 px-5 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {currentIndex === SHORT_DEMO_QUESTIONS.length - 1 ? "View TOC report" : "Next question"}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}